import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Upload,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Image,
  List,
  Tag,
  Tooltip,
  Dropdown,
  Menu,
  Table,
  Spin
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  SendOutlined,
  FolderOutlined,
  ReloadOutlined,
  MoreOutlined,
  HolderOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useResponsive } from '../hooks/useResponsive';
import { useSimpleStore } from '../stores/simpleStore';
import { getApiUrl, getUploadsUrl } from '../utils/api';
import { PageLayout } from './common';

// 인코딩 작업 상태 타입
interface EncodingJob {
  id: string;
  filename: string;
  status: 'pending' | 'analyzing' | 'encoding' | 'saving' | 'completed' | 'error';
  progress: number;
  message: string;
  error?: string | null;
}

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

interface MediaFile {
  id: number;
  title: string;
  original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  media_type?: 'image' | 'video'; // 계산된 필드
  company_id: number;
  company_name: string;
  thumbnail_path?: string;
  duration?: number;
  resolution?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}


interface Device {
  id: number;
  device_id: string;
  device_name: string;
  status: 'online' | 'offline';
}

interface Company {
  id: number;
  name: string;
  code: string;
}

interface Route {
  id: number;
  route_number: string;
  route_name: string;
  city_code: string;
  city_name: string;
}

// 미디어 타입 결정 헬퍼 함수
const getMediaType = (mimeType: string): 'image' | 'video' | 'other' => {
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType?.startsWith('video/')) return 'video';
  return 'other';
};

export default function MediaManagement() {
  const { user, selectedCompanyId, selectedCompanyName, setSelectedCompany } = useSimpleStore();
  const { isMobile } = useResponsive();
  
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<MediaFile | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [form] = Form.useForm();
  
  // API 호출 중복 방지
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // 콘텐츠 관리 내부 업로드 대상 선택
  // ✅ 기본값: 선택된 운수사 있으면 'company', 없으면 'system'
  const [uploadTarget, setUploadTarget] = useState<'system' | 'company'>(
    selectedCompanyId ? 'company' : 'system'
  );

  // 다중 선택 관리
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<MediaFile | null>(null);

  // 수정 관련 상태
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [mediaToEdit, setMediaToEdit] = useState<MediaFile | null>(null);
  const [editForm] = Form.useForm();

  // 인코딩 진행률 상태
  const [encodingJob, setEncodingJob] = useState<EncodingJob | null>(null);
  const [encodingModalVisible, setEncodingModalVisible] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // SSE 연결 정리
  const cleanupEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // 컴포넌트 언마운트 시 SSE 정리
  useEffect(() => {
    return () => {
      cleanupEventSource();
    };
  }, [cleanupEventSource]);

  // 미디어 목록 새로고침 참조 (SSE 콜백에서 사용)
  const loadMediaFilesRef = useRef<() => void>(() => {});

  // SSE로 인코딩 진행률 구독
  const subscribeToEncodingProgress = useCallback((jobId: string, filename: string) => {
    cleanupEventSource();

    setEncodingJob({
      id: jobId,
      filename,
      status: 'pending',
      progress: 0,
      message: '인코딩 준비 중...'
    });
    setEncodingModalVisible(true);

    const sseUrl = getApiUrl(`/api/media/encoding-progress/${jobId}`);
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as EncodingJob;
        setEncodingJob(data);

        // 완료 또는 에러 시 모달 닫기
        if (data.status === 'completed') {
          setTimeout(() => {
            setEncodingModalVisible(false);
            setEncodingJob(null);
            cleanupEventSource();
            // 미디어 목록 새로고침
            loadMediaFilesRef.current();
          }, 2000);
        } else if (data.status === 'error') {
          setTimeout(() => {
            setEncodingModalVisible(false);
            setEncodingJob(null);
            cleanupEventSource();
          }, 3000);
        }
      } catch (err) {
        console.error('SSE 데이터 파싱 오류:', err);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE 연결 오류:', error);
      // 연결 오류 시 재시도하지 않고 정리
      cleanupEventSource();
    };
  }, [cleanupEventSource]);

  // ✅ 선택된 운수사 변경 시 업로드 대상 자동 업데이트
  useEffect(() => {
    if (selectedCompanyId) {
      setUploadTarget('company');
      console.log('✅ 업로드 대상 자동 변경: 선택된 운수사 (' + selectedCompanyName + ')');
    } else {
      setUploadTarget('system');
      console.log('✅ 업로드 대상 자동 변경: 시스템 영상');
    }
  }, [selectedCompanyId, selectedCompanyName]);

  useEffect(() => {
    console.log('MediaManagement useEffect - user:', user, 'selectedCompanyId:', selectedCompanyId);
    if (user?.role === 'admin') {
      console.log('관리자 사용자 확인됨, 회사 목록 로딩 시작');
      loadCompanies();
    }
  }, [user]);

  // 컴포넌트 마운트 시 초기 로딩 (중복 방지를 위해 user 의존성 제거)
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('토큰 페이로드:', payload);
        if (payload.role === 'admin' && companies.length === 0) {
          console.log('토큰에서 관리자 확인, 회사 목록 로딩 (companies 비어있음)');
          loadCompanies();
        }
      } catch (error) {
        console.error('토큰 파싱 실패:', error);
      }
    }
  }, [companies.length]);

  useEffect(() => {
    if (selectedCompanyId !== undefined) {
      console.log('useEffect triggered - loadMediaFiles 호출, selectedCompanyId:', selectedCompanyId);
      loadMediaFiles();
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    if (isLoadingCompanies) {
      console.log('회사 목록 로딩 중이므로 건너뜀');
      return;
    }
    
    setIsLoadingCompanies(true);
    try {
      const token = localStorage.getItem('adminToken');
      console.log('회사 목록 로딩 시작, 토큰:', token ? '존재' : '없음');
      
      const response = await fetch(getApiUrl('/api/companies'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      console.log('회사 목록 응답:', data);
      
      if (data.success) {
        // 시스템 영상 카테고리를 포함한 회사 목록 설정
        const companiesWithSystem = [
          { id: 0, name: '시스템 영상', code: 'SYSTEM_MEDIA' },
          ...(data.data || []).filter((company: any) => company.id !== 0)
        ];
        setCompanies(companiesWithSystem);
        console.log('회사 목록 설정 (시스템 포함):', companiesWithSystem);
        // admin인 경우 운수사 미선택 상태로 시작 (시스템 전체)
        if (companiesWithSystem.length > 0 && selectedCompanyId === undefined) {
          console.log('초기 상태: 운수사 미선택 (시스템 전체)');
          // selectedCompanyId를 null로 설정하여 전체 운수사 상태로 만듦
        }
      } else {
        console.error('회사 목록 로딩 실패:', data.error);
        message.error(data.error || '회사 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('회사 목록 로드 실패:', error);
      message.error('서버 연결에 실패했습니다');
    } finally {
      setIsLoadingCompanies(false);
    }
  };


  const loadMediaFiles = async () => {
    // admin 역할이 아니면서 운수사가 선택되지 않은 경우 리턴
    if (!selectedCompanyId && user?.role !== 'admin') return;

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      // AppConfigManagement와 동일한 방식으로 URL 생성
      let url = '/api/media/list?include_system=true&limit=1000';
      if (selectedCompanyId && selectedCompanyId !== 0) {
        url += `&company_id=${selectedCompanyId}`;
      }

      const fullUrl = getApiUrl(url);

      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('미디어 API 응답:', { url: fullUrl, data, selectedCompanyId });
      if (data.success) {
        const mediaData = data.data || [];
        console.log('설정할 미디어 파일 수:', mediaData.length, mediaData);
        setMediaFiles(mediaData);
      } else {
        console.error('미디어 API 에러:', data.error);
        message.error(data.error || '미디어 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('미디어 목록 로드 실패:', error);
      message.error('서버 연결에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // loadMediaFiles 함수 참조 업데이트 (SSE 콜백에서 사용)
  useEffect(() => {
    loadMediaFilesRef.current = loadMediaFiles;
  });



  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    action: getApiUrl('/api/media/upload'),
    headers: {
      Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
    },
    data: (file) => {
      const title = file.name.split('.').slice(0, -1).join('.'); // 확장자 제거한 파일명을 제목으로 사용
      const targetCompanyId = uploadTarget === 'system' ? 0 : selectedCompanyId;
      console.log('업로드 데이터:', { company_id: targetCompanyId, title, uploadTarget });
      return {
        company_id: targetCompanyId,
        title: title,
        description: `${file.name} 파일 업로드`
      };
    },
    onChange(info) {
      console.log('업로드 상태 변경:', info);
      const { status } = info.file;
      if (status === 'uploading') {
        console.log('업로드 중...', info.file.percent + '%');
      } else if (status === 'done') {
        console.log('업로드 완료:', info.file.response);
        const response = info.file.response;

        // 비디오 인코딩 중인 경우 SSE로 진행률 표시
        if (response?.encodingJobId) {
          console.log('🎬 비디오 인코딩 시작:', response.encodingJobId);
          subscribeToEncodingProgress(response.encodingJobId, info.file.name);
          // SSE에서 완료 후 자동으로 loadMediaFiles 호출됨
        } else {
          // 이미지 또는 인코딩 불필요한 비디오
          message.success(`${info.file.name} 파일이 업로드되었습니다`);
          loadMediaFiles();
        }
      } else if (status === 'error') {
        console.error('업로드 실패:', info.file.error, info.file.response);
        const errorMsg = info.file.response?.error || info.file.error?.message || '알 수 없는 오류';
        message.error(`${info.file.name} 업로드 실패: ${errorMsg}`, 5); // 5초간 표시

        // 콘솔에도 상세 에러 출력
        if (errorMsg.includes('동일한 파일이 이미 업로드되어 있습니다')) {
          console.warn('중복 파일 업로드 시도:', {
            fileName: info.file.name,
            companyId: selectedCompanyId,
            error: errorMsg
          });
        }
      }
    },
    beforeUpload(file) {
      if (uploadTarget === 'company' && (selectedCompanyId === undefined || selectedCompanyId === null)) {
        message.error('운수사별 업로드를 위해서는 좌측에서 운수사를 선택해주세요');
        return false;
      }

      const isValid = file.type.startsWith('image/') || file.type.startsWith('video/');
      if (!isValid) {
        message.error('이미지 또는 비디오 파일만 업로드할 수 있습니다!');
        return false;
      }
      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        message.error('파일 크기는 100MB 미만이어야 합니다!');
        return false;
      }
      return true;
    },
  };

  // 미디어 수정 함수
  const handleEdit = async (values: any) => {
    if (!mediaToEdit || !selectedCompanyId) {
      message.error('수정할 미디어를 선택해주세요');
      return;
    }

    try {
      const response = await fetch(getApiUrl(`/api/media/${mediaToEdit.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({
          title: values.title,
          description: values.description,
          company_id: selectedCompanyId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        message.success('미디어 정보가 성공적으로 수정되었습니다');
        setEditModalVisible(false);
        setMediaToEdit(null);
        editForm.resetFields();
        loadMediaFiles();
      } else {
        console.error('미디어 수정 실패:', data);
        message.error(data.error || '수정에 실패했습니다');
      }
    } catch (error) {
      console.error('미디어 수정 중 오류 발생:', error);
      message.error('수정 중 오류가 발생했습니다');
    }
  };

  const handleUpload = async (values: any) => {
    if (uploadTarget === 'company' && (selectedCompanyId === undefined || selectedCompanyId === null)) {
      message.error('운수사별 업로드를 위해서는 좌측에서 운수사를 선택해주세요');
      return;
    }

    const fileObj = values.file?.[0]?.originFileObj;
    if (!fileObj) {
      message.error('업로드할 파일을 선택해주세요');
      return;
    }

    const isVideo = fileObj.type?.startsWith('video/');
    const filename = fileObj.name;

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      const targetCompanyId = uploadTarget === 'system' ? 0 : selectedCompanyId;

      formData.append('title', values.title);
      formData.append('description', values.description || '');
      formData.append('company_id', targetCompanyId.toString());
      formData.append('file', fileObj);

      console.log('모달 업로드 FormData:', {
        title: values.title,
        description: values.description,
        company_id: targetCompanyId,
        uploadTarget: uploadTarget,
        file: filename,
        isVideo: isVideo
      });

      // 업로드 모달 닫기
      setUploadModalVisible(false);
      form.resetFields();

      // 비디오인 경우 인코딩 진행률 모달 미리 표시 (업로드 중)
      if (isVideo) {
        setEncodingJob({
          id: 'uploading',
          filename: filename,
          status: 'pending',
          progress: 0,
          message: '파일 업로드 중...'
        });
        setEncodingModalVisible(true);
      }

      const response = await fetch(getApiUrl('/api/media/upload'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      console.log('모달 업로드 응답:', data);

      if (data.success) {
        // 비디오 인코딩 중인 경우 SSE로 진행률 표시
        if (data.encodingJobId) {
          console.log('🎬 비디오 인코딩 시작:', data.encodingJobId);
          subscribeToEncodingProgress(data.encodingJobId, filename);
          // SSE에서 완료 후 자동으로 loadMediaFiles 호출됨
        } else {
          // 이미지 또는 인코딩 불필요한 비디오
          if (isVideo) {
            setEncodingModalVisible(false);
            setEncodingJob(null);
          }
          message.success('파일이 성공적으로 업로드되었습니다');
          loadMediaFiles();
        }
      } else {
        // 에러 발생 시 인코딩 모달 닫기
        if (isVideo) {
          setEncodingModalVisible(false);
          setEncodingJob(null);
        }
        console.error('모달 업로드 실패:', data);
        const errorMsg = data.error || '업로드에 실패했습니다';
        message.error(`업로드 실패: ${errorMsg}`, 5); // 5초간 표시

        // 중복 파일 에러인 경우 콘솔에 상세 정보 출력
        if (errorMsg.includes('동일한 파일이 이미 업로드되어 있습니다')) {
          console.warn('중복 파일 업로드 시도 (모달):', {
            title: values.title,
            companyId: selectedCompanyId,
            error: errorMsg
          });
        }
      }
    } catch (error) {
      // 에러 발생 시 인코딩 모달 닫기
      if (isVideo) {
        setEncodingModalVisible(false);
        setEncodingJob(null);
      }
      console.error('업로드 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  const handleDeleteMedia = async (id: number) => {
    if (selectedCompanyId === undefined || selectedCompanyId === null) {
      message.error('회사를 선택해주세요');
      return;
    }

    console.log(`삭제 요청 시작: ID=${id}, company_id=${selectedCompanyId}`);

    try {
      const token = localStorage.getItem('adminToken');
      const url = getApiUrl(`/api/media/${id}?company_id=${selectedCompanyId}`);
      console.log('DELETE 요청 URL:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('DELETE 응답 상태:', response.status, response.statusText);

      const data = await response.json();
      console.log('DELETE 응답 데이터:', data);

      if (data.success) {
        message.success(`파일이 완전히 삭제되었습니다: ${data.deletedFile || ''}`);
        loadMediaFiles();
      } else {
        message.error(data.error || '미디어 파일을 삭제할 수 없습니다');
      }
    } catch (error) {
      console.error('미디어 삭제 실패:', error);
      message.error('서버 오류가 발생했습니다');
    }
  };

  const confirmDeleteMedia = async () => {
    if (!mediaToDelete) return;
    
    console.log(`삭제 확인 클릭: ${mediaToDelete.title} (ID: ${mediaToDelete.id})`);
    await handleDeleteMedia(mediaToDelete.id);
    setDeleteModalVisible(false);
    setMediaToDelete(null);
  };


  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredMedia = mediaFiles.filter(media => {
    if (filterType === 'all') return true;
    return getMediaType(media.mime_type) === filterType;
  });

  const stats = {
    total: mediaFiles.length,
    images: mediaFiles.filter(m => getMediaType(m.mime_type) === 'image').length,
    videos: mediaFiles.filter(m => getMediaType(m.mime_type) === 'video').length,
    totalSize: mediaFiles.reduce((sum, m) => sum + m.file_size, 0),
  };

  const getActionMenu = (media: MediaFile) => ({
    items: [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '수정',
        onClick: () => {
          console.log(`수정 버튼 클릭: ${media.title} (ID: ${media.id})`);
          setMediaToEdit(media);
          editForm.setFieldsValue({
            title: media.title,
            description: media.description || ''
          });
          setEditModalVisible(true);
        }
      },
      {
        key: 'download',
        icon: <DownloadOutlined />,
        label: '다운로드',
        onClick: () => {
          const link = document.createElement('a');
          link.href = getUploadsUrl(media.file_path);
          link.download = media.original_name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '삭제',
        danger: true,
        onClick: () => {
          console.log(`삭제 버튼 클릭: ${media.title} (ID: ${media.id})`);
          setMediaToDelete(media);
          setDeleteModalVisible(true);
        }
      }
    ]
  });

  return (
    <PageLayout
      title="콘텐츠 관리"
      subtitle="이미지, 동영상 등 미디어 파일을 업로드하고 관리하세요"
      actions={[
        {
          key: 'upload',
          label: '파일 업로드',
          type: 'primary',
          icon: <UploadOutlined />,
          onClick: () => setUploadModalVisible(true)
        },
        {
          key: 'refresh',
          label: '새로고침',
          icon: <ReloadOutlined />,
          onClick: loadMediaFiles
        }
      ]}
    >
      {/* 관리자 전용 업로드 대상 선택 */}
      {user?.role === 'admin' && (
        <div style={{
          marginBottom: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            📢 현재 보기: <strong>
              {selectedCompanyId === null || selectedCompanyId === undefined
                ? '시스템 영상만'
                : `${selectedCompanyName} + 시스템 영상`}
            </strong>
          </div>

          <div style={{ fontSize: '12px', color: '#999' }}>
            업로드 대상:
            <Select
              value={uploadTarget}
              onChange={setUploadTarget}
              style={{ marginLeft: 8, minWidth: 180 }}
              size="small"
            >
              <Option value="system">🌐 시스템 영상 (전체 운수사 공용)</Option>
              {selectedCompanyId && (
                <Option value="company">🏢 {selectedCompanyName} 전용 영상</Option>
              )}
            </Select>
          </div>
        </div>
      )}

      {/* 필터 및 액션 버튼 */}
      <div style={{ marginBottom: 24 }}>
        <Space wrap>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 100 }}
            size={isMobile ? 'small' : 'middle'}
          >
            <Option value="all">전체</Option>
            <Option value="image">이미지</Option>
            <Option value="video">동영상</Option>
          </Select>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            {isMobile ? '업로드' : '파일 업로드'}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadMediaFiles}
            loading={loading}
          >
            {isMobile ? '' : '새로고침'}
          </Button>
        </Space>
      </div>

      {/* 통계 카드 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="전체 파일"
              value={stats.total}
              suffix="개"
              prefix={<FileImageOutlined />}
              valueStyle={{ fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="이미지"
              value={stats.images}
              suffix="개"
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="동영상"
              value={stats.videos}
              suffix="개"
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="총 용량"
              value={formatFileSize(stats.totalSize)}
              valueStyle={{ color: '#722ed1', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 미디어 테이블 */}
      <Card>
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{selectedRowKeys.length}개 항목이 선택됨</Text>
          </div>
        )}
        
        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: true
          }}
          dataSource={filteredMedia}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={isMobile ? { x: 600 } : undefined}
          columns={[
            {
              title: '썸네일',
              dataIndex: 'thumbnail',
              key: 'thumbnail',
              width: 80,
              render: (_, media) => (
                <div 
                  style={{ 
                    width: 60, 
                    height: 40, 
                    background: '#f0f0f0',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setPreviewMedia(media);
                    setPreviewVisible(true);
                  }}
                >
                  {media.mime_type?.startsWith('image/') ? (
                    <Image
                      src={getUploadsUrl(media.thumbnail_path || media.file_path)}
                      alt={media.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                      preview={false}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4Z9G..."
                      onError={(e) => {
                        console.log('이미지 로드 실패:', media.thumbnail_path, media.file_path);
                        e.currentTarget.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4Z9G...';
                      }}
                    />
                  ) : (media.mime_type?.startsWith('video/') || media.mime_type?.includes('mp4') || media.file_path?.includes('.mp4')) ? (
                    media.thumbnail_path ? (
                      <Image
                        src={getUploadsUrl(media.thumbnail_path)}
                        alt={media.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                        preview={false}
                        onError={() => console.log('비디오 썸네일 로드 실패:', media.thumbnail_path)}
                      />
                    ) : (
                      <VideoCameraOutlined style={{ fontSize: 16, color: '#999' }} />
                    )
                  ) : (
                    <VideoCameraOutlined style={{ fontSize: 16, color: '#999' }} />
                  )}
                </div>
              )
            },
            {
              title: '제목',
              dataIndex: 'title',
              key: 'title',
              width: 200,
              ellipsis: true,
              render: (text, media) => (
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>{text}</div>
                  <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>
                    파일: {media.filename || media.file_path?.split('/').pop()}
                  </Text>
                </div>
              )
            },
            {
              title: '타입',
              dataIndex: 'mime_type',
              key: 'mime_type',
              width: 80,
              render: (type, record) => {
                const isImage = type?.startsWith('image/');
                const isVideo = type?.startsWith('video/') || type?.includes('mp4') || record.file_path?.includes('.mp4');
                return (
                  <Tag color={isImage ? 'green' : isVideo ? 'blue' : 'gray'}>
                    {isImage ? '이미지' : isVideo ? '동영상' : '기타'}
                  </Tag>
                );
              }
            },
            {
              title: '카테고리',
              dataIndex: 'company_id',
              key: 'category',
              width: 100,
              render: (companyId, record) => {
                if (companyId === 0) {
                  return (
                    <Tag color="purple" style={{ fontSize: 11 }}>
                      🌐 시스템 영상
                    </Tag>
                  );
                } else {
                  return (
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      {record.company_name || '운수사'}
                    </Tag>
                  );
                }
              }
            },
            {
              title: '크기',
              dataIndex: 'file_size',
              key: 'file_size',
              width: 90,
              render: (size) => formatFileSize(size)
            },
            {
              title: '재생시간',
              dataIndex: 'duration',
              key: 'duration',
              width: 90,
              render: (duration) => duration ? 
                `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}` : 
                '-'
            },
            {
              title: '업로드일',
              dataIndex: 'created_at',
              key: 'created_at',
              width: 110,
              render: (date) => new Date(date).toLocaleDateString('ko-KR')
            },
            {
              title: '작업',
              key: 'action',
              width: 60,
              render: (_, media) => (
                <Dropdown menu={getActionMenu(media)} trigger={['click']}>
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )
            }
          ]}
        />
      </Card>

      {/* 업로드 모달 */}
      <Modal
        title="파일 업로드"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 600}
      >
        <Form form={form} layout="vertical" onFinish={handleUpload}>
          <Form.Item
            name="file"
            label="파일 선택"
            rules={[{ required: true, message: '파일을 선택해주세요' }]}
          >
            <Upload 
              maxCount={1} 
              listType="picture"
              beforeUpload={(file) => {
                const isValid = file.type.startsWith('image/') || file.type.startsWith('video/');
                if (!isValid) {
                  message.error('이미지 또는 비디오 파일만 업로드할 수 있습니다!');
                  return false;
                }
                const isLt100M = file.size / 1024 / 1024 < 100;
                if (!isLt100M) {
                  message.error('파일 크기는 100MB 미만이어야 합니다!');
                  return false;
                }
                return false; // 자동 업로드 방지
              }}
              onChange={(info) => {
                console.log('모달 파일 선택:', info);
                // 파일이 선택되면 폼에 설정
                if (info.fileList.length > 0) {
                  form.setFieldsValue({ file: info.fileList });
                }
              }}
            >
              <Button icon={<UploadOutlined />}>파일 선택</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="title"
            label="제목"
            rules={[{ required: true, message: '제목을 입력해주세요' }]}
          >
            <Input placeholder="미디어 파일 제목" />
          </Form.Item>

          <Form.Item name="description" label="설명">
            <TextArea rows={3} placeholder="미디어 파일 설명 (선택사항)" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setUploadModalVisible(false)}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                업로드
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 미디어 미리보기 모달 */}
      <Modal
        title={previewMedia?.title}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 800}
      >
        {previewMedia && (
          <div style={{ textAlign: 'center' }}>
            {previewMedia.mime_type?.startsWith('image/') ? (
              <Image
                src={getUploadsUrl(previewMedia.file_path)}
                alt={previewMedia.title}
                style={{ maxWidth: '100%' }}
              />
            ) : (previewMedia.mime_type?.startsWith('video/') || previewMedia.mime_type?.includes('mp4') || previewMedia.file_path?.includes('.mp4')) ? (
              <video
                controls
                autoPlay
                preload="metadata"
                style={{ maxWidth: '100%', maxHeight: 400 }}
                poster={previewMedia.thumbnail_path ? getUploadsUrl(previewMedia.thumbnail_path) : undefined}
                onLoadStart={() => console.log('Video load started:', previewMedia.file_path)}
                onError={(e) => console.error('Video load error:', e, previewMedia)}
                onCanPlay={() => console.log('Video can play')}
                crossOrigin="anonymous"
                src={getUploadsUrl(previewMedia.file_path)}
              >
                비디오를 재생할 수 없습니다.
              </video>
            ) : (
              <div style={{ padding: '40px', color: '#999' }}>
                지원되지 않는 파일 형식입니다.
              </div>
            )}
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <p><strong>파일명:</strong> {previewMedia.original_name}</p>
              <p><strong>크기:</strong> {formatFileSize(previewMedia.file_size)}</p>
              {previewMedia.resolution && (
                <p><strong>해상도:</strong> {previewMedia.resolution}</p>
              )}
              {previewMedia.duration && (
                <p><strong>재생시간:</strong> {Math.floor(previewMedia.duration / 60)}:{(previewMedia.duration % 60).toString().padStart(2, '0')}</p>
              )}
              <p><strong>업로드일:</strong> {new Date(previewMedia.created_at).toLocaleString()}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        title="파일 삭제 확인"
        open={deleteModalVisible}
        onCancel={() => {
          console.log('삭제 취소됨');
          setDeleteModalVisible(false);
          setMediaToDelete(null);
        }}
        onOk={confirmDeleteMedia}
        okText="삭제"
        okType="danger"
        cancelText="취소"
        confirmLoading={loading}
      >
        {mediaToDelete && (
          <p>
            <strong>"{mediaToDelete.title}"</strong> 파일을 영구적으로 삭제하시겠습니까?
            <br />
            <span style={{ color: '#999', fontSize: '14px' }}>
              원본 파일: {mediaToDelete.original_name}
            </span>
          </p>
        )}
      </Modal>

      {/* 미디어 수정 모달 */}
      <Modal
        title="미디어 수정"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setMediaToEdit(null);
          editForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="title"
            label="제목"
            rules={[{ required: true, message: '제목을 입력해주세요' }]}
          >
            <Input placeholder="미디어 제목을 입력하세요" />
          </Form.Item>

          <Form.Item
            name="description"
            label="설명"
          >
            <TextArea
              rows={3}
              placeholder="미디어 설명을 입력하세요 (선택사항)"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                setMediaToEdit(null);
                editForm.resetFields();
              }}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                수정
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 비디오 인코딩 진행률 모달 */}
      <Modal
        title={
          <Space>
            <VideoCameraOutlined style={{ color: '#1890ff' }} />
            <span>비디오 인코딩 중</span>
          </Space>
        }
        open={encodingModalVisible}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={450}
      >
        {encodingJob && (
          <div style={{ padding: '16px 0' }}>
            {/* 파일명 */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Text strong style={{ fontSize: 16 }}>
                {encodingJob.filename}
              </Text>
            </div>

            {/* 진행률 바 */}
            <Progress
              percent={encodingJob.progress}
              status={
                encodingJob.status === 'error' ? 'exception' :
                encodingJob.status === 'completed' ? 'success' :
                'active'
              }
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              format={(percent) => `${percent}%`}
            />

            {/* 상태 메시지 */}
            <div style={{
              marginTop: 16,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}>
              {encodingJob.status === 'completed' ? (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                  <Text type="success" strong>업로드 완료!</Text>
                </>
              ) : encodingJob.status === 'error' ? (
                <>
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                  <Text type="danger">{encodingJob.message}</Text>
                </>
              ) : (
                <>
                  <LoadingOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                  <Text type="secondary">{encodingJob.message}</Text>
                </>
              )}
            </div>

            {/* 상태별 아이콘 표시 */}
            <div style={{
              marginTop: 24,
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0 20px'
            }}>
              <div style={{ textAlign: 'center', opacity: encodingJob.progress >= 5 ? 1 : 0.3 }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>📁</div>
                <Text type="secondary" style={{ fontSize: 12 }}>분석</Text>
              </div>
              <div style={{ textAlign: 'center', opacity: encodingJob.progress >= 15 ? 1 : 0.3 }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>🎬</div>
                <Text type="secondary" style={{ fontSize: 12 }}>인코딩</Text>
              </div>
              <div style={{ textAlign: 'center', opacity: encodingJob.progress >= 90 ? 1 : 0.3 }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>💾</div>
                <Text type="secondary" style={{ fontSize: 12 }}>저장</Text>
              </div>
              <div style={{ textAlign: 'center', opacity: encodingJob.progress >= 100 ? 1 : 0.3 }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
                <Text type="secondary" style={{ fontSize: 12 }}>완료</Text>
              </div>
            </div>

            {/* 안내 메시지 */}
            {encodingJob.status !== 'completed' && encodingJob.status !== 'error' && (
              <div style={{
                marginTop: 20,
                padding: 12,
                background: '#f0f5ff',
                borderRadius: 6,
                textAlign: 'center'
              }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Android 기기 호환을 위해 H.264 Baseline 포맷으로 변환 중입니다.
                  <br />
                  파일 크기에 따라 1~2분 정도 소요될 수 있습니다.
                </Text>
              </div>
            )}
          </div>
        )}
      </Modal>

    </PageLayout>
  );
}